import { configureStore } from '@reduxjs/toolkit';
import reduceReducers from 'reduce-reducers';
import { combineReducers } from 'redux';

import { marketStockExchangeSlice } from '!views/Market/StockExchange/_marketStockExchange.slice';
import {
  authSlice,
  insertOrderPrimarySlice,
  loginSlice,
  logoutSlice,
  menusSlice,
  onboardingSlice,
  otpSlice,
  trlTopInstrumentsSlice,
} from '»components/index';
import { questionnaireSlice } from '»components/Questionnaire/redux';

import {
  fundsSlice,
  publicOfferSlice,
  tradingSlice,
  walletSlice,
} from '../../views';
import { i18nSlice, tracerSlice } from '../features';
import {
  axiosMiddleware,
  reduxSagasMiddleware,
  runSagas,
} from '../middlewares';
import { accountBalanceSlice } from './accountBalance';
import { dbConfigurationsSlice } from './dbConfigurations';
import { initialStore } from './initial.store';
import { openedFileSlice } from './openedFile';
import { loadState, saveState } from './saveStore/sessionStorage';
import { userAttributesSlice } from './userAttributes';
import { userContactsSlice } from './userContacts';
import { userInfoSlice } from './userInfo';
import { userInstrumentsSlice } from './userInstruments';
import { userSecuritiesAccountsSlice } from './userSecuritiesAccounts';

const configStore = () => {
  const sessionState = loadState();
  const middleware = [axiosMiddleware, reduxSagasMiddleware];
  const preloadedState = { ...initialStore, ...sessionState };

  const reducer = combineReducers({
    tracing: tracerSlice.reducer,
    i18n: i18nSlice.reducer,
    auth: reduceReducers(
      authSlice.reducer,
      loginSlice.reducer,
      logoutSlice.reducer,
      onboardingSlice.reducer,
      otpSlice.reducer,
    ),
    menus: menusSlice.reducer,
    userInfo: userInfoSlice.reducer,
    userContacts: userContactsSlice.reducer,
    userAttributes: userAttributesSlice.reducer,
    userSecuritiesAccounts: userSecuritiesAccountsSlice.reducer,
    userInstruments: userInstrumentsSlice.reducer,
    accountBalance: accountBalanceSlice.reducer,
    publicOffer: publicOfferSlice.reducer,
    wallet: walletSlice.reducer,
    trading: tradingSlice.reducer,
    marketStockExchange: marketStockExchangeSlice.reducer,
    funds: fundsSlice.reducer,
    topInstruments: trlTopInstrumentsSlice.reducer,
    insertOrderPrimary: insertOrderPrimarySlice.reducer,
    dbConfigurations: dbConfigurationsSlice.reducer,
    openedFile: openedFileSlice.reducer,
    questionnaire: questionnaireSlice.reducer,
  });

  const store = configureStore({
    reducer,
    preloadedState,
    middleware: (getDefaultMiddleware) => {
      return getDefaultMiddleware().concat(middleware);
    },
  });

  runSagas();

  store.subscribe(() => {
    const state = store.getState();
    saveState({
      tracing: state.tracing,
      i18n: state.i18n,
      auth: state.auth,
      menus: state.menus,
      userInfo: state.userInfo,
      userContacts: state.userContacts,
      userAttributes: state.userAttributes,
      userSecuritiesAccounts: state.userSecuritiesAccounts,
      accountBalance: state.accountBalance,
      publicOffer: state.publicOffer,
      userInstruments: state.userInstruments,
      wallet: state.wallet,
      trading: state.trading,
      marketStockExchange: state.marketStockExchange,
      funds: state.funds,
      topInstruments: state.topInstruments,
      insertOrderPrimary: state.insertOrderPrimary,
      dbConfigurations: state.dbConfigurations,
      openedFile: state.openedFile,
      questionnaire: state.questionnaire,
    });
  });

  return store;
};

export const appStore = configStore();
